import type { Place, RackBoundary } from '../types/warehouse.types';
import { isMouseOverPlace } from './hitDetection';
/**
 * Get a Place object by its place_id
 * @param places - Map of place_id to Place
 * @param placeId - The place_id to look up (format: "A-1-2-3")
 * @returns Place object or undefined if not found
 */
export function getPlaceById(places: Map<string, Place>, placeId: string): Place | undefined {
  // First try direct lookup by place_id (e.g. "A-1-2-3")
  const direct = places.get(placeId);
  if (direct) return direct;

  // numeric IDs (e.g. storage_place_id). Try to find
  // a place whose `id` matches the numeric value of `placeId`.
  const numeric = Number(placeId);
  if (!Number.isNaN(numeric)) {
    for (const p of places.values()) {
      if (p.id === numeric) return p;
    }
  }

  return undefined;
}

/**
 * Get multiple Place objects by their place_ids
 * @param places - Map of place_id to Place
 * @param placeIds - Array of place_ids to look up
 * @returns Array of Place objects (only found places)
 */
export function getPlacesByIds(places: Map<string, Place>, placeIds: string[]): Place[] {
  const result: Place[] = [];
  placeIds.forEach((placeId) => {
    const place = getPlaceById(places, placeId);
    if (place) {
      result.push(place);
    }
  });
  return result;
}

/**
 * Get row ID from a Place
 * @param place - Place object
 * @returns Row ID in format "A1" (areaCode + row number)
 */
export function getRowId(place: Place): string {
  return `${place.areaCode}${place.row}`;
}

/**
 * Filter places by row name/ID
 * @param places - Array or Map of places
 * @param rowName - Row name to match (e.g., "A1")
 * @param activeLevels - Optional set of active levels to filter by
 * @returns Filtered array of places
 */
export function getPlacesByRow(
  places: Map<string, Place> | Place[],
  rowName: string,
  activeLevels?: Set<number>
): Place[] {
  const placesArray = places instanceof Map ? Array.from(places.values()) : places;
  
  return placesArray.filter(place => {
    // Check row match
    const rowId = getRowId(place);
    if (rowId !== rowName) return false;
    
    // Check level filter if provided
    if (activeLevels && !activeLevels.has(place.level)) return false;
    
    return true;
  });
}

/**
 * Group places by place number (column)
 * Useful for rendering places in a grid layout
 * 
 * @param places - Array of places to group
 * @returns Map of place number to array of places at that position
 */
export function groupPlacesByColumn(places: Place[]): Map<number, Place[]> {
  const columnMap = new Map<number, Place[]>();
  
  places.forEach(place => {
    if (!columnMap.has(place.placeNumber)) {
      columnMap.set(place.placeNumber, []);
    }
    columnMap.get(place.placeNumber)!.push(place);
  });
  
  return columnMap;
}

/**
 * Group places by vertical column position (baseX, baseY, row, placeNumber)
 * Used for rendering column-arranged pallets in isometric view
 * 
 * @param places - Array of places to group
 * @returns Map of column key to array of places in that column
 */
export function groupPlacesByVerticalColumn(places: Place[]): Map<string, Place[]> {
  const columnMap = new Map<string, Place[]>();
  
  places.forEach(place => {
    const columnKey = `${place.baseX.toFixed(2)}-${place.baseY.toFixed(2)}-${place.row}-${place.placeNumber}`;
    
    if (!columnMap.has(columnKey)) {
      columnMap.set(columnKey, []);
    }
    columnMap.get(columnKey)!.push(place);
  });
  
  return columnMap;
}

/**
 * Find the topmost place at a given screen position
 * Iterates through places from top level to bottom to find the first match
 *
 * @param places - Map of places to search
 * @param screenX - X coordinate in screen space
 * @param screenY - Y coordinate in screen space
 * @returns The topmost place at the position, or null if none found
 */
export function findPlaceAtPosition(
  places: Map<string, Place>,
  screenX: number,
  screenY: number,
): Place | null {
  const maxLevel = Math.max(...Array.from(places.values()).map(p => p.level));
  const levelOrder = Array.from({ length: maxLevel }, (_, i) => maxLevel - i);

  for (const level of levelOrder) {
    const placesAtLevel = Array.from(places.values()).filter(p => p.level === level);
    for (const place of placesAtLevel) {
      if (isMouseOverPlace(screenX, screenY, place)) {
        return place;
      }
    }
  }

  return null;
}

/**
 * Get rack boundaries for a specific row
 * @param rackBoundaries - Array of all rack boundaries
 * @param rowName - The row name to filter by (e.g., "A01")
 * @returns Array of rack boundaries for the specified row
 */
export function getRackBoundariesByRow(
  rackBoundaries: RackBoundary[],
  rowName: string
): RackBoundary[] {
  return rackBoundaries.filter(rack => rack.rowName === rowName);
}
